import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth-context'
import { useWorkspace } from '@/lib/workspace-context'

export interface Lead {
  id: string
  email: string
  name: string | null
  eventId: string
  eventName: string
  doiConfirmed: boolean
  doiConfirmedAt: string | null
  createdAt: string
  customFields: Record<string, unknown>
}

export function useLeads(eventId?: string) {
  const { user } = useAuth()
  const { activeWorkspace } = useWorkspace()

  return useQuery({
    queryKey: ['leads', activeWorkspace?.id, eventId],
    queryFn: async (): Promise<Lead[]> => {
      if (!user || !activeWorkspace) return []

      let query = supabase
        .from('leads')
        .select(`
          id,
          email,
          name,
          event_id,
          doi_confirmed,
          doi_confirmed_at,
          created_at,
          custom_fields,
          events(name)
        `)
        .eq('tenant_id', activeWorkspace.id)
        .eq('archived', false)
        .order('created_at', { ascending: false })

      if (eventId) {
        query = query.eq('event_id', eventId)
      }

      const { data, error } = await query

      if (error) {
        console.error('Error fetching leads:', error)
        throw error
      }

      return (data || []).map((lead) => ({
        id: lead.id,
        email: lead.email,
        name: lead.name,
        eventId: lead.event_id,
        eventName: (lead.events as { name: string })?.name || '',
        doiConfirmed: lead.doi_confirmed,
        doiConfirmedAt: lead.doi_confirmed_at,
        createdAt: lead.created_at,
        customFields: (lead.custom_fields as Record<string, unknown>) || {},
      }))
    },
    enabled: !!user && !!activeWorkspace,
    refetchInterval: 10000, // Refresh every 10 seconds
  })
}

export function useLeadStats() {
  const { user } = useAuth()
  const { activeWorkspace } = useWorkspace()

  return useQuery({
    queryKey: ['lead-stats', activeWorkspace?.id],
    queryFn: async () => {
      if (!user || !activeWorkspace) return null

      const { data, error } = await supabase
        .from('leads')
        .select('id, doi_confirmed')
        .eq('tenant_id', activeWorkspace.id)
        .eq('archived', false)

      if (error) throw error

      const leads = data || []
      const total = leads.length
      const confirmed = leads.filter((l) => l.doi_confirmed).length
      const pending = total - confirmed

      return {
        total,
        confirmed,
        pending,
        conversionRate: total > 0 ? Math.round((confirmed / total) * 100) : 0,
      }
    },
    enabled: !!user && !!activeWorkspace,
  })
}
